// Drop-in replacement for the old @trpc/client `trpcClient` proxy.
// Exposes `trpcClient.<group>.<proc>.query(input)` / `.mutate(input)` shape so
// imperative call sites (RainbowKit signin handler, auth refresh hook) keep
// working without changes.

import { proceduresClient } from './procedures';

type AnyProc = (...args: any[]) => Promise<any>;

interface ProcCallable<P extends AnyProc> {
  query: P;
  mutate: P;
}

function wrap<P extends AnyProc>(proc: P): ProcCallable<P> {
  return {
    query: proc,
    mutate: proc,
  };
}

function build(): any {
  const out: any = {};
  for (const [group, groupProcs] of Object.entries(proceduresClient)) {
    out[group] = {};
    for (const [name, proc] of Object.entries(groupProcs)) {
      if (typeof proc === 'function') {
        out[group][name] = wrap(proc as AnyProc);
      } else if (proc && typeof proc === 'object') {
        out[group][name] = {};
        for (const [subName, subProc] of Object.entries(proc)) {
          out[group][name][subName] = wrap(subProc as AnyProc);
        }
      }
    }
  }
  return out;
}

type ProceduresClient = typeof proceduresClient;

type TrpcClientShape = {
  [G in keyof ProceduresClient]: {
    [P in keyof ProceduresClient[G]]: ProceduresClient[G][P] extends AnyProc
      ? ProcCallable<ProceduresClient[G][P]>
      : ProceduresClient[G][P] extends Record<string, AnyProc>
        ? {
            [SP in keyof ProceduresClient[G][P]]: ProceduresClient[G][P][SP] extends AnyProc
              ? ProcCallable<ProceduresClient[G][P][SP]>
              : never;
          }
        : never;
  };
};

export const trpcClient = build() as TrpcClientShape;
